/**
 * Campaign Card Component
 *
 * Displays a single email campaign card with preview image, metrics, and actions.
 * Combines preview display from TemplateCard with campaign-specific information.
 *
 * @created 2025-10-30
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmailCampaign } from '@/api/email';
import {
  Mail,
  Edit,
  Eye,
  Trash2,
  MoreVertical,
  Camera,
  Calendar,
  Users,
  UserMinus,
  Filter,
  Clock
} from 'lucide-react';

interface CampaignCardProps {
  campaign: EmailCampaign;
  onEdit: (campaign: EmailCampaign) => void;
  onDelete?: (campaign: EmailCampaign) => void;
  onCapturePreview: (campaign: EmailCampaign) => void;
  onPreview?: (campaign: EmailCampaign) => void;
  userRole?: string;
}

/**
 * CampaignCard Component
 *
 * Displays campaign information including:
 * - Preview image (or placeholder)
 * - Campaign name and subject
 * - Status badge
 * - Metrics (recipients, schedule type, audience type, filters)
 * - Sent campaign metrics (unsubscribed)
 * - Action menu (edit, preview, capture, delete)
 *
 * @example
 * <CampaignCard
 *   campaign={campaign}
 *   onEdit={handleEdit}
 *   onCapturePreview={handleCapturePreview}
 * />
 */
const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  onEdit,
  onDelete,
  onCapturePreview,
  onPreview,
  userRole
}) => {
  // Determine available actions based on campaign status
  const canEdit = campaign.status === 'draft';
  const canDelete = campaign.status === 'draft' && onDelete;
  const canCapture = true; // All users can capture campaign previews

  /**
   * Get status badge variant based on campaign status
   */
  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'sent':
        return 'default'; // Green
      case 'scheduled':
        return 'secondary'; // Blue
      case 'sending':
        return 'outline'; // Gray with border
      case 'failed':
        return 'destructive'; // Red
      case 'draft':
      default:
        return 'secondary'; // Gray
    }
  };

  /**
   * Get status display label
   */
  const getStatusLabel = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  /**
   * Format date and time for scheduled campaigns
   */
  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Format audience filters for display
   * Extracts meaningful filter values and formats them for display
   * Returns an array of filter strings to be displayed on separate lines
   */
  const formatFilters = (filters?: Record<string, any>): string[] => {
    if (!filters || Object.keys(filters).length === 0) return ['No filters set'];

    const filterParts: string[] = [];

    // Number of orders range
    if (filters.min_orders || filters.max_orders) {
      const min = filters.min_orders && filters.min_orders > 0 ? filters.min_orders : null;
      const max = filters.max_orders && filters.max_orders > 0 ? filters.max_orders : null;

      if (min && max) {
        filterParts.push(`Number of orders: ${min} - ${max}`);
      } else if (min) {
        filterParts.push(`Number of orders: ${min}+`);
      } else if (max) {
        filterParts.push(`Number of orders: ≤${max}`);
      }
    }

    // Total spent range
    if (filters.min_total_spent || filters.max_total_spent) {
      const min = filters.min_total_spent && filters.min_total_spent > 0 ? filters.min_total_spent : null;
      const max = filters.max_total_spent && filters.max_total_spent > 0 ? filters.max_total_spent : null;

      if (min && max) {
        filterParts.push(`Total spend: $${min} - $${max}`);
      } else if (min) {
        filterParts.push(`Total spend: $${min}+`);
      } else if (max) {
        filterParts.push(`Total spend: ≤$${max}`);
      }
    }

    // Average order value range
    if (filters.min_avg_order_value || filters.max_avg_order_value) {
      const min = filters.min_avg_order_value && filters.min_avg_order_value > 0 ? filters.min_avg_order_value : null;
      const max = filters.max_avg_order_value && filters.max_avg_order_value > 0 ? filters.max_avg_order_value : null;

      if (min && max) {
        filterParts.push(`Avg order value: $${min} - $${max}`);
      } else if (min) {
        filterParts.push(`Avg order value: $${min}+`);
      } else if (max) {
        filterParts.push(`Avg order value: ≤$${max}`);
      }
    }

    // Last order date range
    if (filters.last_order_from_date || filters.last_order_to_date) {
      const fromDate = filters.last_order_from_date
        ? new Date(filters.last_order_from_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
        : null;
      const toDate = filters.last_order_to_date
        ? new Date(filters.last_order_to_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
        : null;

      if (fromDate && toDate) {
        filterParts.push(`Last order: ${fromDate} - ${toDate}`);
      } else if (fromDate) {
        filterParts.push(`Last order: ${fromDate}+`);
      } else if (toDate) {
        filterParts.push(`Last order: <${toDate}`);
      }
    }

    return filterParts.length > 0 ? filterParts : ['No filters set'];
  };

  return (
    <Card className="group hover:shadow-md transition-all duration-200 hover:border-primary/50">
      <CardContent className="p-0">
        {/* Preview Image */}
        <div className="relative w-full aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100 rounded-t-lg overflow-hidden">
          {campaign.preview_image_url ? (
            <img
              src={campaign.preview_image_url}
              alt={campaign.campaign_name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No Preview</p>
              </div>
            </div>
          )}

          {/* Status Badge */}
          <div className="absolute top-2 right-2">
            <Badge variant={getStatusVariant(campaign.status)}>
              {getStatusLabel(campaign.status)}
            </Badge>
          </div>

          {/* Action Menu (shows on hover) */}
          <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 bg-white/90 hover:bg-white shadow-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(campaign);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                )}

                {onPreview && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview(campaign);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </DropdownMenuItem>
                )}

                {canCapture && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onCapturePreview(campaign);
                    }}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Capture Preview
                  </DropdownMenuItem>
                )}

                {canDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete!(campaign);
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Campaign Info */}
        <div className="p-4 space-y-3">
          {/* Name and Subject */}
          <div>
            <h3
              className="font-medium text-sm line-clamp-1 mb-1"
              title={campaign.campaign_name}
            >
              {campaign.campaign_name}
            </h3>
            <p className="text-xs text-gray-600 line-clamp-1" title={campaign.campaign_subject}>
              {campaign.campaign_subject}
            </p>
          </div>

          {/* Metrics Grid */}
          <div className="space-y-2">
            {/* Recipients */}
            <div className="flex items-center gap-2 text-xs">
              <Users className="h-3 w-3 text-gray-500" />
              <span className="text-gray-500">Recipients:</span>
              <span className="font-medium ml-auto">
                {campaign.recipient_count?.toLocaleString() || 0}
              </span>
            </div>

            {/* Unsubscribed */}
            <div className="flex items-center gap-2 text-xs">
              <UserMinus className="h-3 w-3 text-gray-500" />
              <span className="text-gray-500">Unsubscribed:</span>
              <span className="font-medium ml-auto">
                {campaign.unsubscribed_count?.toLocaleString() || 0}
              </span>
            </div>

            {/* Schedule Type */}
            <div className="flex items-center gap-2 text-xs">
              <Clock className="h-3 w-3 text-gray-500" />
              <span className="text-gray-500">Schedule:</span>
              <span className="font-medium ml-auto capitalize">
                {campaign.schedule_type || 'Immediate'}
              </span>
            </div>

            {/* Scheduled Time */}
            {campaign.schedule_time && (
              <div className="flex items-center gap-2 text-xs">
                <Calendar className="h-3 w-3 text-gray-500" />
                <span className="text-gray-500">Scheduled:</span>
                <span className="font-medium ml-auto text-right">
                  {formatDateTime(campaign.schedule_time)}
                </span>
              </div>
            )}

            {/* Audience Type */}
            <div className="flex items-center gap-2 text-xs">
              <Filter className="h-3 w-3 text-gray-500" />
              <span className="text-gray-500">Audience:</span>
              <span className="font-medium ml-auto capitalize">
                {campaign.audience_type || 'All'}
              </span>
            </div>

            {/* Filters (for filtered audiences) */}
            {campaign.audience_type === 'filtered' && campaign.audience_filters && (
              <div className="flex items-start gap-2 text-xs">
                <Filter className="h-3 w-3 text-gray-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-500 flex-shrink-0">Filters:</span>
                <div className="ml-auto text-right text-xs max-w-[200px] space-y-1">
                  {formatFilters(campaign.audience_filters).map((filter, index) => (
                    <div key={index} className="font-medium text-gray-700">
                      {filter}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer: Created Date */}
          {campaign.created_at && (
            <div className="text-xs text-gray-500 pt-2 border-t">
              Created {formatDate(campaign.created_at)}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CampaignCard;
